/**
 * Tipo padrão para mapeamento de domínio, com ID e descrição.
 *
 * @typedef {Object.<string, {id: string, descricao: string}>} situacaoIdDescricao
 */

/**
 * @classdesc Classe base para "decoradores" que permite acessar os atributos "id" e "descricao" de objetos (enums) de tipos que mapeiam domínios de status.
 * @readonly
 */
export default class SituacaoIdDescricaoBase {
    /**
     * @private
     * @type {situacaoIdDescricao} Qualquer objeto que tenha as propriedades "id" e "descricao"
     */
    SITUACAO_ID_DESCRICAO;

    /**
     * Construtor da classe base Decorator
     *
     * @param {situacaoIdDescricao} enumSituacaoNoFormatoIdDescricao Objeto que tenha as propriedades "id" e "descricao"
     */
    constructor(enumSituacaoNoFormatoIdDescricao) {
        this.SITUACAO_ID_DESCRICAO = enumSituacaoNoFormatoIdDescricao;
    }

    /**
     * Retorna o ID da descrição.
     *
     * @public
     * @param {string} descricao Descrição da situação.
     * @returns {string} O ID da descrição ou uma string vazia caso não encontre.
     */
    getIdByDescricao(descricao) {
        let objProcurado = this.getObjectByProperty('descricao', descricao);

        if (objProcurado === null) {
            return '';
        }

        return objProcurado.id;
    }

    /**
     * Retorna a descrição correspondente ao ID.
     *
     * @public
     * @param {string} id Identificador da situação.
     * @returns {string} A descrição correspondente ao ID ou uma string vazia caso não encontre.
     */
    getDescricao(id) {
        return this.getValorPropriedade(id, 'descricao');
    }

    /**
     * Retorna o valor da propriedade correspondente ao ID.
     *
     * @public
     * @param {string} id Identificador da situação.
     * @param {string} property O nome da propriedade do objeto.
     * @returns {string} O valor correspondente à propriedade relacionada ao ID ou uma string vazia caso não encontre.
     */
    getValorPropriedade(id, property) {
        if (
            Object.prototype.hasOwnProperty.call(this.SITUACAO_ID_DESCRICAO, id) &&
            typeof this.SITUACAO_ID_DESCRICAO[id][property] !== 'undefined'
        ) {
            return this.SITUACAO_ID_DESCRICAO[id][property];
        }

        let objProcurado = this.getObjectById(id);

        if (objProcurado === null || !(property in objProcurado)) {
            return '';
        }

        return objProcurado[property];
    }

    /**
     * Retorna um objeto com todos os atributos e valores da enum correspondentes ao ID procurado.
     *
     * @public
     * @param {string} id O ID a ser procurado.
     * @returns {Object} Um objeto com todos os atributos e valores da enum correspondentes ao ID procurado ou null caso não encontre.
     */
    getObjectById(id) {
        return this.getObjectByProperty('id', id);
    }

    /**
     * Retorna um objeto com todos os atributos e valores da enum correspondentes ao valor da propriedade especificada.
     *
     * @public
     * @param {string} nomePropriedade O nome da propriedade do objeto.
     * @param {string} valorPropriedade O valor da propriedade do objeto.
     * @returns {Object} Um objeto com todos os atributos e valores da enum correspondentes ao valor da propriedade especificada ou null caso não encontre.
     */
    getObjectByProperty(nomePropriedade, valorPropriedade) {
        if (typeof nomePropriedade === 'undefined' || typeof valorPropriedade === 'undefined') {
            return null;
        }

        let objSituacao = null;

        Object.values(this.SITUACAO_ID_DESCRICAO).forEach((situacao) => {
            if (nomePropriedade in situacao && situacao[nomePropriedade] === valorPropriedade) {
                objSituacao = {};
                for (let attr in situacao) {
                    if (Object.prototype.hasOwnProperty.call(situacao, attr)) {
                        objSituacao[attr] = situacao[attr];
                    }
                }
            }
        });

        return objSituacao;
    }

    /**
     * Retorna um objeto que representa a Enumeration reduzida apenas com a propriedade (chave/valor) que corresponde ao ID procurado.
     *
     * @public
     * @param {string} id O ID a ser procurado.
     * @returns {Object} Um objeto que representa a própria Enumeration filtrada apenas com a entrada que contém o ID procurado ou null caso não encontre.
     */
    getItemEnumById(id) {
        if (typeof id === 'undefined') {
            return null;
        }

        let objItemEnum = null;
        let objSituacao = null;

        Object.entries(this.SITUACAO_ID_DESCRICAO).forEach(([key, situacao]) => {
            if (situacao.id === id) {
                if (objItemEnum === null) {
                    objItemEnum = {};
                }

                objSituacao = {};
                for (let attr in situacao) {
                    if (Object.prototype.hasOwnProperty.call(situacao, attr)) {
                        objSituacao[attr] = situacao[attr];
                    }
                }

                objItemEnum[key] = objSituacao;
            }
        });

        return objItemEnum;
    }
}
